import React, { useEffect, useState } from "react";
import { message, Card, Form, Select, Input, DatePicker, Row, Col, Button, Flex } from "antd";
import { dateFormat, dateFormatIso, menuLinks, notifyMessages, requiredMessages } from "../../utils/constants";
import { PageHeader } from "../../components/common/pageHeader";
import { Link, useParams, useNavigate } from "react-router";
import { SaveOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUserContext } from "../../context/currentUserContext.js";
import dayjs from 'dayjs';
import DocumentsServices from "../../services/documentsServices";
import ContentLoader from "../../components/common/contentLoader";
import { fetchSuppliersList } from "../../state/slices/suppliersListSlice.js";


const EditDocument = () => {
    const urlParams = useParams();
    const { currentUser } = useCurrentUserContext();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [documentData, setDocumentData] = useState(null);

    const divisionsList = useSelector(state => state.divisions.divisions);
    const suppliersList = useSelector(state => state.suppliers.suppliers);
    const anomaliesList = useSelector(state => state.anomalies.anomalies);
    const documentTypesList = useSelector(state => state.docTypes.docTypes);

    useEffect(() => {
        getDocumentData(urlParams.id, currentUser.id);
    }, []);


    useEffect(() => {
        if (!currentUser) return;
        dispatch(fetchSuppliersList(currentUser.lastSelectedCategory, false, currentUser.id));
    }, [dispatch, currentUser]);

    const getDocumentData = async (documentId, userId) => {
        setLoadingButton(true);
        try {
            const respData = await DocumentsServices.getDocument(documentId, userId);
            if (respData.success) {
                setDocumentData(respData.responseObject.value.document);
                form.setFieldsValue(mapToFormValues(respData.responseObject.value.document));
            } else {
                message.error(notifyMessages.errorAccured);
            }
        } catch (error) {
            message.error(notifyMessages.errorAccured);
        } finally {
            setLoadingButton(false);
        }
    };

    const mapToFormValues = (document) => ({
        typeID: document.typeID,
        plate: document.plate,
        flux: document.flux,
        anomalyID: document.anomalyID || null,
        divisionID: document.divisionID || null,
        supplierID: document.supplierID || null,
        dateAdded: document.dateAdded ? dayjs(document.dateAdded) : null,
        year: document.year,
        notes: document.notes,
    });

    const mapToApiValues = (values) => ({

        DocumentID: urlParams.id,
        TypeID: values.typeID,
        Plate: values.plate,
        Flux: values.flux,
        AnomalyID: values.anomalyID || null,
        DivisionID: values.divisionID || null,
        SupplierID: values.supplierID || null,
        DateAdded: values.dateAdded ? values.dateAdded.format(dateFormatIso) : null,
        Year: values.year,
        Notes: values.notes,
        LastEditedUser: values.lastEditedUser
    });

    const updateData = async (values) => {
        setLoadingButton(true);
        values["lastEditedUser"] = currentUser.id;
        const apiValues = mapToApiValues(values);
        try {
            const respData = await DocumentsServices.updateDocument(apiValues);
            if (respData.success) {
                message.success(notifyMessages.operationSuccess);
                navigate(`${menuLinks.raportiControllo}/view/id/${urlParams.id}`);
            } else {
                message.error(respData.ErrorDetail?.ErrorMessage || notifyMessages.errorAccured);
            }
        } catch (error) {
            message.error(notifyMessages.errorAccured);
        } finally {
            setLoadingButton(false);
        }
    };

    const handleYearNumberInput = (e) => {
        const inputValue = e.target.value;
        const onlyNums = inputValue.replace(/\D/g, '');
        form.setFieldsValue({ year: onlyNums });
    };

    return (
        <div className="edit-document">

            <PageHeader title={"Modifica documento"} />

            {!documentData && <ContentLoader />}

            {documentData && (
                <>
                    <Card title="Dati documento" size="small" className="editCard">
                        <Form
                            id="editDocument"
                            form={form}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            style={{ maxWidth: 900, width: "100%" }}
                            onFinish={updateData}
                            onValuesChange={() => setFieldsChanged(true)}
                        >
                            <Form.Item
                                label="Tipo"
                                name="typeID"
                                rules={[{ required: true, message: requiredMessages.requiredField }]}>

                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Tipo"
                                    options={documentTypesList}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Targa impianto"
                                name="plate"
                                rules={[{ required: true, message: requiredMessages.requiredField }]}>

                                <Input />
                            </Form.Item>

                            <Form.Item
                                label="Batch"
                                name="flux" >

                                <Input />
                            </Form.Item>

                            <Form.Item
                                label="Anomalie"
                                name="anomalyID" >

                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Anomalie"
                                    options={anomaliesList}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Divisione"
                                name="divisionID" >

                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Divisione"
                                    options={divisionsList}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Manutentore"
                                name="supplierID"
                                rules={[{ required: true, message: requiredMessages.requiredField }]}>

                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Manutentore"
                                    options={suppliersList}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Data caricamento"
                                name="dateAdded"
                                rules={[{ required: true, message: requiredMessages.requiredField }]}>

                                <DatePicker format={dateFormat} style={{ width: "100%" }} />
                            </Form.Item>

                            <Form.Item
                                label="Anno"
                                name="year"
                                rules={[{ required: true, message: requiredMessages.requiredField }]}>

                                <Input onChange={handleYearNumberInput} value={form.getFieldValue('year')} />
                            </Form.Item>

                            <Form.Item
                                label="Note"
                                name="notes">

                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Form>
                    </Card>

                    <Row gutter={24} >
                        <Col span={24} className="modifyDocButtons">
                            <Flex gap="small" wrap>
                                <Button type="default">
                                    <Link to={`${menuLinks.raportiControllo}/view/id/${urlParams.id}`}>Annulla</Link>
                                </Button>

                                <Button
                                    loading={loadingButton}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!fieldsChanged}
                                    form="editDocument"
                                >
                                    <SaveOutlined /> Salva
                                </Button>
                            </Flex>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default EditDocument;
